'use client';

import Link from 'next/link';
import AffiliateLink from '@/components/common/AffiliateLink';
import GfBento from '@/components/common/GfBento';
import { trackAffiliateClick } from '@/lib/affiliates/trackClick';
import CountryFAQ from '@/components/country/CountryFAQ';
import GfSectionMark from '@/components/common/GfSectionMark';
import GfArticleCards from '@/components/common/GfArticleCards';
import GfServiceLedger from '@/components/common/GfServiceLedger';
import GfSubnav from '@/components/common/GfSubnav';
import GfVerticalHero from '@/components/common/GfVerticalHero';
import GfSteps from '@/components/common/GfSteps';
import GfCta from '@/components/common/GfCta';
import GfProviderCard from '@/components/common/GfProviderCard';
import GfPromoCodes from '@/components/country/GfPromoCodes';
import { toursLedgerConfig } from '@/components/common/ledgerConfigs';
import { ICONS } from '@/components/common/gfIcons';
import { renderTablerIcon } from '@/utils/iconRenderer';
import { makeCountryDisplay } from '@/lib/content/declension';
import { t } from '@/i18n/translations';

// "Что можно посмотреть" cards: choose an icon by what the tour type is about
// (titles come from the CMS, in either language). A CMS-supplied Tabler name
// wins; otherwise we keyword-match, falling back to a compass for unknown types.
const TOUR_TYPE_ICONS = [
    [/(пеш|walk|hik|trek|экскурс)/i, 'TbWalk'],
    [/(мор|sea|boat|sail|cruise|яхт|катер|лодоч)/i, 'TbSailboat'],
    [/(природ|парк|nature|park|эко|eco|сад|garden)/i, 'TbTrees'],
    [/(истор|архитект|histor|architect|музе|museum|cultur|культур|замок|castle)/i, 'TbBuildingCastle'],
    [/(вин|гастро|еда|food|wine|culinary|кухн|dining|taste|дегуст)/i, 'TbChefHat'],
    [/(актив|adventure|active|спорт|sport|extrem|экстрим|rafting|рафтинг|каяк)/i, 'TbRun'],
];
const tourTypeIcon = (b) => {
    if (typeof b.icon === 'string' && /^Tb[A-Z]/.test(b.icon)) {
        const named = renderTablerIcon(b.icon, { size: 26, className: 'ri' });
        if (named) return named;
    }
    const hay = `${b.titleRu || ''} ${b.titleEn || ''}`;
    const match = TOUR_TYPE_ICONS.find(([re]) => re.test(hay));
    return (match && renderTablerIcon(match[1], { size: 26, className: 'ri' })) || ICONS.compass;
};

export default function ToursPageContent({ content = {}, lang = 'ru', toursArticles = [], promotions = [], background, country, countryName, localNotes = null }) {
    const isRu = lang === 'ru';
    const countrySlug = content.countrySlug || country;
    const { cName, cCase } = makeCountryDisplay(countryName, countrySlug, isRu);
    const sp = t(lang, 'servicePage');     // общие тексты сервис-страниц
    const vp = t(lang, 'toursPage');       // специфика туров

    const cities = Array.isArray(content.cities) ? content.cities : [];
    const providers = Array.isArray(content.providers) ? content.providers : [];
    const tourTypeBlocks = Array.isArray(content.tourTypeBlocks) ? content.tourTypeBlocks : [];
    const howItWorks = Array.isArray(content.howItWorks) ? content.howItWorks : [];
    const faqItems = Array.isArray(content.faq) ? content.faq : []; // already {question, answer}

    // Направления — бенто-плитки (как «Что посмотреть» на страницах городов);
    // внешний клик на туры трекается как affiliate_click.
    const cityBentoItems = cities.map((city, i) => {
        const cityPageHref = city.slug ? `/${lang}/${countrySlug}/${city.slug}` : null;
        return {
            id: city.slug || i,
            img: city.image,
            alt: city.name,
            title: city.name,
            excerpt: city.description,
            actions: [
                city.href && {
                    id: 'tours',
                    label: vp.cityTours,
                    href: city.href,
                    target: '_blank',
                    rel: 'nofollow noopener noreferrer sponsored',
                    onClick: () => trackAffiliateClick({
                        provider: city.provider,
                        vertical: 'tours',
                        country: country || countrySlug,
                        position: 'city_card',
                        label: city.name,
                    }),
                },
                cityPageHref && {
                    id: 'details',
                    label: vp.cityDetails,
                    href: cityPageHref,
                    className: 'ghost',
                },
            ].filter(Boolean),
        };
    });

    const has = {
        cities: cities.length > 0,
        local: Boolean(localNotes),
        providers: providers.length > 0,
        types: tourTypeBlocks.length > 0,
        how: howItWorks.length > 0,
        compare: true,
        promos: promotions?.length > 0,
        faq: faqItems.length > 0,
        articles: toursArticles?.length > 0,
    };

    // Реальные туры страны из Sanity (content.ledgerTours); без них — демо-данные
    const ledger = toursLedgerConfig(lang, content.ledgerTours);
    const hasTourTypeFilter = Array.isArray(ledger.filters) && ledger.filters.some((f) => f.key === '_groupType');
    // С реальными турами таблица цен — первая секция: до неё было 16 карточек городов, и первая
    // ссылка Viator стояла на 8-м экране телефона (обход 01.10.2026). Демо-таблицу вперёд не ставим.
    const ledgerFirst = has.compare && Array.isArray(content.ledgerTours) && content.ledgerTours.length > 0;

    const anchors = [
        ledgerFirst && { href: '#compare', label: vp.anchorPopular },
        has.cities && { href: '#cities', label: vp.anchorCities },
        has.local && { href: '#local', label: localNotes.eyebrow },
        has.providers && { href: '#providers', label: sp.anchor.providers },
        has.types && { href: '#types', label: vp.anchorTypes },
        has.compare && !ledgerFirst && { href: '#compare', label: vp.anchorPopular },
        has.how && { href: '#how', label: vp.anchorHow },
        has.promos && { href: '#promocodes', label: sp.anchor.discounts },
        has.faq && { href: '#faq', label: sp.anchor.faq },
        has.articles && { href: '#articles', label: sp.anchor.articles },
    ].filter(Boolean);

    const compareSection = has.compare && (
        <section className="gf-sec" id="compare" style={{ paddingTop: ledgerFirst ? undefined : 0 }}>
            <GfSectionMark eyebrow={vp.popularEyebrow} title={vp.popularTitle} />
            <p className="gf-lead">{vp.popularLead(hasTourTypeFilter)}</p>
            <GfServiceLedger lang={lang} {...ledger} />
        </section>
    );

    const blockUrl = (b) => (b.useSingleUrl ? b.url : (isRu ? b.urlRu : b.urlEn) || b.url);

    return (
        <div className="gf-root">
            <GfVerticalHero background={background} title={content.title} sub={content.description}>
                {ledgerFirst
                    ? <a className="pri" href="#compare">{vp.heroPrices} <b>↓</b></a>
                    : has.cities && <a className="pri" href="#cities">{vp.heroBrowse} <b>→</b></a>}
                {has.types && <a className="sec" href="#types">{vp.heroTypes}</a>}
            </GfVerticalHero>

            <GfSubnav lang={lang} leaf={vp.leaf} anchors={anchors}
                crumbs={[cName && { href: `/${lang}/${countrySlug}`, label: cName }]} />

            {ledgerFirst && compareSection}

            {/* ── DESTINATIONS (affiliate city cards) ──────────── */}
            {has.cities && (
                <section className="gf-sec" id="cities" style={{ paddingTop: ledgerFirst ? 0 : undefined }}>
                    <GfSectionMark eyebrow={vp.citiesEyebrow} title={content.citiesTitle || vp.citiesTitle(cName ? cCase('prepositional') : '')} />
                    {content.citiesSubtitle && <p className="gf-lead">{content.citiesSubtitle}</p>}
                    <GfBento items={cityBentoItems} excerptAll />
                </section>
            )}

            {/* ── LOCAL SCENE (country-unique) ─────────────────── */}
            {has.local && (
                <section className="gf-sec gf-localnet" id="local" style={{ paddingTop: has.cities ? 0 : undefined }}>
                    <GfSectionMark eyebrow={localNotes.eyebrow} tag={cName || ''} title={localNotes.heading} />
                    {localNotes.intro && <p className="gf-lead">{localNotes.intro}</p>}
                    {localNotes.points.length > 0 && (
                        <ul className="gf-localnet-list">
                            {localNotes.points.map((point, i) => (
                                <li key={i}>{point}</li>
                            ))}
                        </ul>
                    )}
                    {localNotes.pricing && <p className="gf-localnet-pricing">{localNotes.pricing}</p>}
                    {localNotes.outro && <p className="gf-localnet-outro">{localNotes.outro}</p>}
                </section>
            )}

            {/* ── PROVIDERS ────────────────────────────────────── */}
            {has.providers && (
                <section className="gf-sec" id="providers" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={sp.eyebrow.providers} tag={providers.length} title={vp.providersTitle} />
                    {content.providersSubtitle && <p className="gf-lead">{content.providersSubtitle}</p>}
                    <div className="gf-grid gf-grid-center">
                        {providers.map((p) => (
                            <GfProviderCard key={p.slug} name={p.name} logo={p.logo} description={p.description}>
                                <Link className="visit" href={`/${lang}/${countrySlug}/tours/${p.slug}`}>{sp.details}</Link>
                            </GfProviderCard>
                        ))}
                    </div>
                </section>
            )}

            {/* ── TOUR TYPES (affiliate type blocks) ───────────── */}
            {has.types && (
                <section className="gf-sec" id="types" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={vp.typesEyebrow} title={vp.typesTitle} />
                    {content.tourTypesSubtitle && <p className="gf-lead">{content.tourTypesSubtitle}</p>}
                    <div className="gf-scen gf-scen-feat cols-3">
                        {tourTypeBlocks.map((b) => {
                            const url = blockUrl(b);
                            const title = isRu ? b.titleRu : b.titleEn;
                            return (
                                <article className="sc tt" key={b._key}>
                                    <div className="ri-row">{tourTypeIcon(b)}<h3>{title}</h3></div>
                                    {b.description && <p>{b.description}</p>}
                                    {url && (
                                        <AffiliateLink href={url} vertical="tours" country={country || countrySlug} position="tour_type" className="go">
                                            {vp.typeDetails}
                                        </AffiliateLink>
                                    )}
                                </article>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* ── POPULAR TOURS (ledger) ───────────────────────── */}
            {!ledgerFirst && compareSection}

            {/* ── HOW IT WORKS ─────────────────────────────────── */}
            {has.how && (
                <section className="gf-sec" id="how" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={sp.eyebrow.guide} tag={`${howItWorks.length} ${sp.stepsWord(howItWorks.length)}`} title={content.howItWorksTitle || vp.howTitle} />
                    {content.howItWorksSubtitle && <p className="gf-lead">{content.howItWorksSubtitle}</p>}
                    <GfSteps steps={howItWorks} />
                </section>
            )}

            {/* ── PROMO CODES ──────────────────────────────────── */}
            {has.promos && (
                <section className="gf-sec" id="promocodes" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={sp.eyebrow.discounts} tag={sp.tag.current} title={vp.promosTitle} />
                    <p className="gf-lead">{vp.promoLead}</p>
                    <GfPromoCodes promotions={promotions} lang={lang} country={country || countrySlug} />
                </section>
            )}

            {/* ── FAQ ──────────────────────────────────────────── */}
            {has.faq && (
                <section className="gf-sec" id="faq" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={sp.eyebrow.qa} tag={sp.tag.faq} title={sp.popularQuestions} />
                    {content.faqSubtitle && <p className="gf-lead">{content.faqSubtitle}</p>}
                    <CountryFAQ faqItems={faqItems} lang={lang} variant="gf" />
                    {content.faqLink?.href && content.faqLink?.text && (
                        <div style={{ marginTop: 26 }}><Link className="gf-alllink" href={content.faqLink.href}>{content.faqLink.text}</Link></div>
                    )}
                </section>
            )}

            {/* ── ARTICLES ─────────────────────────────────────── */}
            {has.articles && (
                <section className="gf-sec" id="articles" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={sp.eyebrow.resources} tag={sp.tag.blog} title={vp.articlesTitle} allHref={`/${lang}/blog/category/tours-and-attractions`} allLabel={sp.allArticles} />
                    <p className="gf-lead">{vp.articlesLead}</p>
                    <GfArticleCards posts={toursArticles} lang={lang} variant="bento" bucket />
                </section>
            )}

            <GfCta
                eyebrow={vp.ctaEyebrow}
                title={vp.ctaTitle(cName ? (isRu ? cCase('accusative') : cName) : '')}
            >
                {has.cities && <a className="gf-btn" href="#cities">{vp.ctaBtn}</a>}
            </GfCta>
        </div>
    );
}
